import React, { ReactNode } from 'react'
import { GestureResponderEvent, View } from 'react-native'

import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { BadgeShape, BadgeVariant } from '../../badge/constants'
import { getFontStyles } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { ActivityBadge } from '../activityBadge/ActivityBadge'
import { BodyText } from '../bodyText/BodyText'
import { PressableOpacity } from '../pressableOpacity/PressableOpacity'

export type GenericListItemProps = {
  title?: string | ReactNode
  subText?: string | ReactNode
  icon?: ReactNode
  badgeContent?: string | number | ReactNode | null
  badgeShape?: BadgeShape
  badgeVariant?: BadgeVariant
  withBoldBadge?: boolean
  listPressedBackgroundColor?: string
  itemClickHandler?: () => void
  rightItems?: ReactNode[]
  fullBorder?: boolean
  onClick?: (event: GestureResponderEvent) => void
  inactive?: boolean
  isFirstItem?: boolean
  isLastItem?: boolean
  testID?: string
  largeText?: boolean
  boldText?: boolean
  height?: string
  leftContainerColumnFlex?: boolean
  containerStyle?: CSSObject
  innerContainerStyle?: CSSObject
  textStyle?: CSSObject
  showTextOverflow?: boolean
  disabled?: boolean
  subTextBetween?: boolean
  badgeOnTop?: boolean
  alignBottomBorder?: boolean
}

const GenericListItemContainer = styled(PressableOpacity)<{
  inactive?: boolean
  fullBorder?: boolean
  isFirstItem?: boolean
  isLastItem?: boolean
  containerStyle?: CSSObject
  theme: ThemeType
}>(({ inactive, fullBorder, isFirstItem, isLastItem, containerStyle, theme: { spacing, colors } }) => ({
  opacity: inactive ? '0.5' : '',
  border: fullBorder ? '1px solid' : '',
  borderColor: fullBorder ? colors.borderDefault : '',
  borderTopLeftRadius: isFirstItem ? spacing['12px'] : '',
  borderTopRightRadius: isFirstItem ? spacing['12px'] : '',
  borderBottomLeftRadius: isLastItem ? spacing['12px'] : '',
  borderBottomRightRadius: isLastItem ? spacing['12px'] : '',
  ...containerStyle,
}))

const GenericListItemInnerContainer = styled.View<{
  fullBorder?: boolean
  isMobileSized: boolean
  isLastItem?: boolean
  theme?: ThemeType
  height?: string
  largeText?: boolean
  innerContainerStyle?: CSSObject
  alignBottomBorder: boolean
}>(
  ({
    largeText,
    fullBorder,
    height,
    isMobileSized,
    isLastItem,
    innerContainerStyle,
    theme: { spacing, colors },
    alignBottomBorder,
  }) => ({
    ...(!alignBottomBorder && {
      borderBottomWidth: !fullBorder && !isLastItem ? '1px' : '',
      borderBottomColor: !fullBorder && !isLastItem ? colors.borderDefault : '',
      padding: largeText ? `${spacing['16px']} ${spacing['24px']}` : spacing['24px'],
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'row',
      justifyContent: 'space-between',
    }),
    ...(alignBottomBorder && {
      paddingTop: largeText ? `${spacing['16px']}` : spacing['24px'],
      paddingHorizontal: spacing['24px'],
    }),
    height,
    margin: isMobileSized ? '0 17px' : '',
    ...innerContainerStyle,
  }),
)

const GenericListItemAlignBorder = styled.View<{
  alignBottomBorder: boolean
  fullBorder?: boolean
  isLastItem?: boolean
  theme: ThemeType
  isMobileSized: boolean
  largeText?: boolean
}>(({ alignBottomBorder, fullBorder, isLastItem, theme: { spacing, colors }, isMobileSized, largeText }) => ({
  ...(alignBottomBorder && {
    borderBottomWidth: !fullBorder && !isLastItem ? '1px' : '',
    borderBottomColor: !fullBorder && !isLastItem ? colors.borderDefault : '',
    paddingBottom: largeText ? spacing['16px'] : spacing['24px'],
  }),
  gap: !isMobileSized ? spacing['24px'] : spacing['0px'],
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  justifyContent: 'space-between',
  width: '100%',
}))

const SubtextContainer = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  textTransform: 'none',
  ...getFontStyles(theme.colors, theme.fontFamilyMetadata).body.small,
  lineHeight: theme.spacing['20px'],
  color: theme.colors.textSecondary,
}))

const LeftContainer = styled.View<{
  columnFlex?: boolean
  fixedWidth?: boolean
}>(({ columnFlex, fixedWidth }) => ({
  display: 'flex',
  alignItems: columnFlex ? 'start' : 'center',
  flexDirection: columnFlex ? 'column' : 'row',
  flexShrink: 1,
  overflow: 'hidden',
  ...(fixedWidth && {
    width: '200px',
  }),
}))

const TextContainer = styled.View({
  display: 'flex',
  flexDirection: 'column',
  flexShrink: 1,
  overflow: 'hidden',
})

const Title = styled.View<{
  theme: ThemeType
  largeText?: boolean
  boldText?: boolean
  textStyle?: CSSObject
}>(({ largeText, boldText, textStyle, theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  textTransform: 'none',
  marginBottom: theme.spacing['4px'],
  ...(largeText
    ? getFontStyles(theme.colors, theme.fontFamilyMetadata).body.large
    : getFontStyles(theme.colors, theme.fontFamilyMetadata).title.xsmall),
  fontWeight: boldText ? '500' : '400',
  lineHeight: '22px',
  ...textStyle,
}))

const Icon = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['16px'],
}))

const BadgeContainer = styled.View<{ hasLeftMargin?: boolean }>(({ hasLeftMargin }) => ({
  marginLeft: hasLeftMargin ? '10px' : '0px',
  flexShrink: 0,
  alignSelf: 'flex-start',
}))

const TitleTextContainer = styled.View({
  flexShrink: 1,
  overflow: 'hidden',
  alignItems: 'flex-start',
})

const RightContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  paddingLeft: theme.spacing['12px'],
}))

export const GenericListItem: React.FC<GenericListItemProps> = ({
  title,
  icon,
  subText,
  badgeContent,
  badgeShape,
  badgeVariant = BadgeVariant.NEUTRAL,
  withBoldBadge,
  listPressedBackgroundColor,
  rightItems,
  fullBorder,
  onClick,
  inactive,
  isFirstItem,
  isLastItem,
  testID,
  largeText,
  boldText,
  height = '83px',
  leftContainerColumnFlex,
  containerStyle,
  innerContainerStyle,
  textStyle,
  showTextOverflow,
  disabled,
  subTextBetween,
  badgeOnTop,
  alignBottomBorder = false,
  ...props
}) => {
  const { breakpoints, colors } = useTheme()
  const onLinkClick = (event: GestureResponderEvent) => {
    if (onClick) {
      onClick(event)
    }
  }

  const badgeElement = badgeContent ? (
    <BadgeContainer hasLeftMargin={!badgeOnTop}>
      <ActivityBadge
        variant={badgeVariant}
        shape={badgeShape ?? BadgeShape.ROUND}
        bold={withBoldBadge}
        preserveCase={true}
        testID={tID('GenericListItem-badge')}
        text={badgeContent as any}
      />
    </BadgeContainer>
  ) : null

  return (
    <GenericListItemContainer
      pressedBackgroundColor={onClick ? listPressedBackgroundColor || colors.backgroundSecondary : ''}
      inactive={inactive}
      fullBorder={fullBorder}
      onPress={onLinkClick}
      isFirstItem={isFirstItem}
      isLastItem={isLastItem}
      testID={testID}
      containerStyle={containerStyle}
      disabled={disabled}
    >
      <GenericListItemInnerContainer
        height={height}
        isMobileSized={breakpoints.isMobileSized}
        fullBorder={fullBorder}
        isLastItem={isLastItem}
        largeText={largeText}
        innerContainerStyle={innerContainerStyle}
        alignBottomBorder={alignBottomBorder}
        {...props}
      >
        <GenericListItemAlignBorder
          alignBottomBorder={alignBottomBorder}
          isMobileSized={breakpoints.isMobileSized}
          fullBorder={fullBorder}
          isLastItem={isLastItem}
          largeText={largeText}
        >
          <LeftContainer columnFlex={leftContainerColumnFlex} fixedWidth={subTextBetween}>
            {icon ? <Icon>{icon}</Icon> : null}
            <TextContainer>
              {badgeOnTop && badgeElement}
              <Title largeText={largeText} boldText={boldText} textStyle={textStyle}>
                <TitleTextContainer>
                  {showTextOverflow ? <BodyText text={title} /> : <BodyText numberOfLines={1} text={title} />}
                </TitleTextContainer>
                {!badgeOnTop && badgeElement}
              </Title>
              {subText && !subTextBetween ? (
                <SubtextContainer data-test-id='GenericListItem-subText'>{subText}</SubtextContainer>
              ) : null}
            </TextContainer>
          </LeftContainer>
          {subTextBetween && (
            <View style={{ flex: 1 }}>
              <SubtextContainer data-test-id='GenericListItem-subText'>{subText}</SubtextContainer>
            </View>
          )}
          {rightItems ? <RightContainer>{rightItems}</RightContainer> : null}
        </GenericListItemAlignBorder>
      </GenericListItemInnerContainer>
    </GenericListItemContainer>
  )
}
